import {
  BASELINES,
  baselineLabel,
  deltaFor,
  type DayStats,
} from '../../lib/dayStats';
import type { DayStatsState } from './useDayStats';
import { StatRow } from './StatRow';
import { Alert, Card, SEGMENTED_TRACK, segmentedItem, SkeletonRows } from '../ui';
import { CheckIcon, ChecklistIcon, PlusIcon } from '../icons';

/**
 * How today is going, against the day before it or the same weekday a week back.
 *
 * Three numbers that do not share a scope, on purpose. "Tasks completed" is the
 * whole workspace, because finishing something is a shared result and a task
 * records who created and who owns it but never who ticked it. "Tasks I
 * created" is the caller's own, because filing work is a habit rather than an
 * outcome. The labels are what carry that difference -- flattening them into a
 * matching pair would make the card quietly wrong, so the "I" is load-bearing.
 */
export function DayStatsCard({
  stats,
  wins,
}: {
  stats: DayStatsState;
  /**
   * Today's score, handed over by the win list below rather than re-derived.
   *
   * The server answers it too, and that is what fills this row on load and on
   * any screen where the list is not mounted. But once the list is on the page
   * it is the authority: it knows about a tick the instant it happens, where
   * asking the server again would put a round trip behind every click on the
   * card immediately below this one.
   */
  wins?: { done: number; total: number } | null;
}) {
  const { baseline, setBaseline, today, base, loading, loadError, baselineError } = stats;
  const vs = baselineLabel(baseline);

  // The list below wins when it is there; the server's answer fills in when it
  // is not. Same shape either way, so only this line knows the difference.
  const live: Pick<DayStats, 'winsDone' | 'winsTotal'> = wins
    ? { winsDone: wins.done, winsTotal: wins.total }
    : { winsDone: today?.winsDone ?? 0, winsTotal: today?.winsTotal ?? 0 };

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-4 py-3">
        <h2 className="text-lg font-semibold tracking-tight">Today</h2>

        {/* The day card's control, same track and same skin -- these are the
            only two segmented pickers on this page and they should not disagree
            about what a two-way choice looks like. It names the baseline, so the
            rows below do not have to. */}
        <div role="group" aria-label="Compare against" className={SEGMENTED_TRACK}>
          {BASELINES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              aria-pressed={baseline === value}
              onClick={() => setBaseline(value)}
              className={segmentedItem(baseline === value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loadError ? (
        <div className="p-4">
          <Alert>{loadError}</Alert>
        </div>
      ) : loading || !today ? (
        // Taller than the default row: these are 24px numerals, and a skeleton
        // sized for text would let the card jump when the numbers land.
        <div className="px-4 py-3">
          <SkeletonRows count={3} rowClassName="h-9" />
        </div>
      ) : (
        <ul className="divide-y divide-stone-100">
          <StatRow
            icon={<CheckIcon className="h-3.5 w-3.5" />}
            label="Tasks completed"
            value={today.tasksCompleted}
            delta={deltaFor(today.tasksCompleted, base?.tasksCompleted ?? null, 'up')}
            vs={vs}
            note={baselineError}
          />

          <StatRow
            icon={<PlusIcon className="h-3.5 w-3.5" />}
            label="Tasks I created"
            value={today.tasksCreated}
            // Neutral on purpose: filing more is not better than filing fewer,
            // and a green arrow here would be the card congratulating a filling
            // inbox.
            delta={deltaFor(today.tasksCreated, base?.tasksCreated ?? null, 'neutral')}
            vs={vs}
            note={baselineError}
          />

          <StatRow
            icon={<ChecklistIcon className="h-3.5 w-3.5" />}
            label="Wins ticked"
            value={live.winsDone}
            suffix={`/${live.winsTotal}`}
            delta={winsDelta(live, base)}
            vs={vs}
            note={winsNote(live, base, vs) ?? baselineError}
          />
        </ul>
      )}
    </Card>
  );
}

/**
 * No list on either side means nothing to compare.
 *
 * The one row here that can honestly answer "no data", because it is the only
 * one whose denominator can be empty. A count of tasks nobody completed is
 * zero; a list nobody wrote is not a list of zero things. Concretely: at nine
 * in the morning, before today's list is typed, this must not report yesterday's
 * five wins as `▼-5` on the one card meant to be encouraging.
 */
function winsDelta(today: Pick<DayStats, 'winsDone' | 'winsTotal'>, base: DayStats | null): ReturnType<typeof deltaFor> {
  if (today.winsTotal === 0 || !base || base.winsTotal === 0) return { kind: 'none' };
  // The done count and not the ratio: a percentage is a second unit in a
  // three-row card, and 1/1 to 2/3 is more work done but reads as a fall. The
  // ratio is not lost -- it is the "/6" beside the number.
  return deltaFor(today.winsDone, base.winsDone, 'up');
}

function winsNote(today: Pick<DayStats, 'winsTotal'>, base: DayStats | null, vs: string): string | null {
  if (today.winsTotal === 0) return 'No list today yet';
  if (base && base.winsTotal === 0) return `No list ${vs}`;
  return null;
}
